import { useId, type ReactElement } from 'react';
import type { ContributeFormState } from '@/lib/contribute-form';

const TITLE_MIN_LENGTH = 2;
const PLACEHOLDER_TITLE = 'Untitled piece';
const COMPOSER_PLACEHOLDER = '—';

export type ContributeLivePreviewProps = {
  state: ContributeFormState;
  duplicateMatchCount: number;
  duplicatesAcknowledged: boolean;
};

type ChecklistItem = {
  id: string;
  label: string;
  done: boolean;
};

export function ContributeLivePreview({
  state,
  duplicateMatchCount,
  duplicatesAcknowledged,
}: ContributeLivePreviewProps): ReactElement {
  const headingId = useId();
  const trimmedTitle = state.title.trim();
  const previewTitle = trimmedTitle.length === 0 ? PLACEHOLDER_TITLE : trimmedTitle;
  const previewComposer = derivePreviewComposer(state);

  const titleDone = trimmedTitle.length >= TITLE_MIN_LENGTH;
  const formDone = state.forms.length > 0;
  const settingsDone = state.settings.some((s) => s.keyId !== null);
  const duplicatesGate = duplicateMatchCount === 0 || duplicatesAcknowledged;
  const submitReady = titleDone && formDone && settingsDone && duplicatesGate;

  const checklist: ChecklistItem[] = [
    { id: 'title', label: 'Title', done: titleDone },
    { id: 'form', label: 'Form', done: formDone },
    {
      id: 'settings',
      label: 'At least one setting with a key',
      done: settingsDone,
    },
    { id: 'submit-ready', label: 'Submit-ready', done: submitReady },
  ];

  return (
    <aside
      aria-labelledby={headingId}
      className="flex flex-col gap-4 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-soft)] p-4"
    >
      <h2 id={headingId} className="text-sm font-semibold text-[var(--ink)]">
        Submission preview
      </h2>

      <div className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4">
        <div className="font-display text-base font-semibold leading-tight text-[var(--ink)]">
          {previewTitle}
        </div>
        <div className="mt-1 text-xs text-[var(--ink-3)]">{previewComposer}</div>
        {state.forms.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {state.forms.map((form) => (
              <span
                key={form}
                className="rounded bg-[var(--bg-soft)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]"
              >
                {form}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 text-[11px] text-[var(--ink-3)]">
          {arrangementsLabel(state.settings.length)}
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {checklist.map((item) => (
          <li key={item.id} className="flex items-center gap-2 text-sm text-[var(--ink)]">
            <input
              type="checkbox"
              checked={item.done}
              readOnly
              aria-label={item.label}
              className="h-4 w-4 cursor-default"
            />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-[var(--ink-3)]">
        Submissions are checked for duplicates and basic accuracy by community editors.
      </p>
    </aside>
  );
}

function derivePreviewComposer(state: ContributeFormState): string {
  if (state.composerMode === 'traditional') return 'Traditional';
  if (state.composerMode === 'unknown') return 'Unknown';
  if (state.composerMode === 'named') {
    const trimmed = state.composerName.trim();
    return trimmed.length > 0 ? trimmed : COMPOSER_PLACEHOLDER;
  }
  return COMPOSER_PLACEHOLDER;
}

function arrangementsLabel(count: number): string {
  return count === 1 ? '1 arrangement' : `${count} arrangements`;
}
